import { useEffect, useState } from "react";
import type {
  Cliente,
  Expediente,
  FrecuenciaCuotas,
  Materia,
  ModalidadHonorario,
  Moneda,
  TipoDescuentoHonorario,
} from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select, TextArea } from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

type Regla = "art2" | "art10" | "art15" | "manual";
type ResultadoApelacion = "confirmada" | "revocada_total" | "revocada_parcial";

interface CuotaPreview {
  numero: number;
  vencimiento: string;
  capital: number;
  interes: number;
  importeTotal: number;
}

type DesgloseCalculo =
  | {
      tipo: "estimado";
      articulo: string;
      jusAplicados: number;
      porcentaje: number | null;
      valorJus: number;
      subtotalLegal: number;
      fuente: string;
    }
  | { tipo: "requiere_criterio_profesional"; motivo: string };

const MODALIDADES: ModalidadHonorario[] = ["Porcentaje", "Monto Fijo", "Mixto", "Regulado Judicialmente", "Extrajudicial"];
const FRECUENCIAS: FrecuenciaCuotas[] = ["semanal", "quincenal", "mensual"];

// Presupuestador de Honorarios — componente único reutilizado desde
// EconomiaPage, ClientesPage y PestanaEconomico (mismo flujo, distinto
// contexto inicial). El honorario que resulta de este wizard ES el
// registro que ya vive en la tabla `honorarios` desde el primer paso —
// no hay una entidad "presupuesto" separada que después se convierte.
export function PresupuestadorHonorarios({
  abierto,
  onCerrar,
  clienteIdInicial,
  expedienteIdInicial,
  onCreado,
}: {
  abierto: boolean;
  onCerrar: () => void;
  clienteIdInicial?: string;
  expedienteIdInicial?: string;
  onCreado?: () => void;
}) {
  const { mostrar } = useToast();
  const [paso, setPaso] = useState(1);
  const [guardando, setGuardando] = useState(false);
  const [calculando, setCalculando] = useState(false);

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [materias, setMaterias] = useState<Materia[]>([]);

  // Paso 1 — Contexto
  const [clienteId, setClienteId] = useState(clienteIdInicial ?? "");
  const [expedienteId, setExpedienteId] = useState(expedienteIdInicial ?? "");
  const [materiaId, setMateriaId] = useState("");
  const [moneda, setMoneda] = useState<Moneda>("ARS");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));

  // Paso 2 — Cálculo legal
  const [regla, setRegla] = useState<Regla>("manual");
  const [montoProceso, setMontoProceso] = useState("");
  const [baseCalculada, setBaseCalculada] = useState("");
  const [resultadoApelacion, setResultadoApelacion] = useState<ResultadoApelacion>("confirmada");
  const [honorarioManual, setHonorarioManual] = useState("");
  const [resultadoSimulado, setResultadoSimulado] = useState<DesgloseCalculo | null>(null);
  const [valorJusResuelto, setValorJusResuelto] = useState<number | null>(null);

  // Paso 3 — Propuesta comercial
  const [modalidad, setModalidad] = useState<ModalidadHonorario>("Porcentaje");
  const [subtotal, setSubtotal] = useState("");
  const [descuentoTipo, setDescuentoTipo] = useState<TipoDescuentoHonorario | "Ninguno">("Ninguno");
  const [descuentoValor, setDescuentoValor] = useState("");
  const [motivoDescuento, setMotivoDescuento] = useState("");
  const [anticipo, setAnticipo] = useState("0");
  const [cantidadCuotas, setCantidadCuotas] = useState("1");
  const [frecuencia, setFrecuencia] = useState<FrecuenciaCuotas>("mensual");
  const [primeraFecha, setPrimeraFecha] = useState(new Date().toISOString().slice(0, 10));
  const [interesPct, setInteresPct] = useState("0");
  const [cuotasPreview, setCuotasPreview] = useState<CuotaPreview[]>([]);

  useEffect(() => {
    if (!abierto) return;
    api.get<{ items: Cliente[] }>("/clientes").then((d) => setClientes(d.items)).catch(() => undefined);
    api.get<{ items: Materia[] }>("/catalogos/materias").then((d) => setMaterias(d.items)).catch(() => undefined);
  }, [abierto]);

  useEffect(() => {
    if (!abierto || !clienteId) {
      setExpedientes([]);
      return;
    }
    api
      .get<{ items: Expediente[] }>(`/expedientes?cliente_id=${clienteId}`)
      .then((d) => {
        setExpedientes(d.items);
        const actual = d.items.find((e) => e.id === expedienteId);
        if (actual) setMateriaId(actual.materia_id);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, clienteId]);

  function reiniciar() {
    setPaso(1);
    setClienteId(clienteIdInicial ?? "");
    setExpedienteId(expedienteIdInicial ?? "");
    setMateriaId("");
    setMoneda("ARS");
    setFecha(new Date().toISOString().slice(0, 10));
    setRegla("manual");
    setMontoProceso("");
    setBaseCalculada("");
    setResultadoApelacion("confirmada");
    setHonorarioManual("");
    setResultadoSimulado(null);
    setValorJusResuelto(null);
    setModalidad("Porcentaje");
    setSubtotal("");
    setDescuentoTipo("Ninguno");
    setDescuentoValor("");
    setMotivoDescuento("");
    setAnticipo("0");
    setCantidadCuotas("1");
    setFrecuencia("mensual");
    setPrimeraFecha(new Date().toISOString().slice(0, 10));
    setInteresPct("0");
    setCuotasPreview([]);
  }

  function cerrar() {
    reiniciar();
    onCerrar();
  }

  async function calcular() {
    if (regla === "manual") {
      setSubtotal(honorarioManual);
      setPaso(3);
      return;
    }
    setCalculando(true);
    try {
      const cuerpo: Record<string, unknown> = { regla };
      if (regla === "art2") cuerpo.montoProceso = montoProceso ? Number(montoProceso) : null;
      if (regla === "art15") {
        cuerpo.baseCalculada = Number(baseCalculada);
        cuerpo.resultadoApelacion = resultadoApelacion;
      }
      const datos = await api.post<{ resultado: DesgloseCalculo; valorJus: { valor: number } }>("/honorarios/simular", cuerpo);
      setResultadoSimulado(datos.resultado);
      setValorJusResuelto(datos.valorJus.valor);
      if (datos.resultado.tipo === "estimado") {
        setSubtotal(String(datos.resultado.subtotalLegal));
        setModalidad(regla === "art10" ? "Monto Fijo" : "Porcentaje");
      }
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo calcular.", "error");
    } finally {
      setCalculando(false);
    }
  }

  const subtotalNum = Number(subtotal) || 0;
  const montoDescuentoLocal =
    descuentoTipo === "Porcentaje"
      ? subtotalNum * ((Number(descuentoValor) || 0) / 100)
      : descuentoTipo === "Importe"
        ? Number(descuentoValor) || 0
        : 0;
  const totalOfrecidoLocal = Math.max(subtotalNum - montoDescuentoLocal, 0);

  async function previsualizarCuotas() {
    try {
      const datos = await api.post<{ cuotas: CuotaPreview[] }>("/honorarios/plan-cuotas", {
        montoTotal: totalOfrecidoLocal,
        anticipo: Number(anticipo) || 0,
        cantidadCuotas: Number(cantidadCuotas) || 1,
        frecuencia,
        primeraFecha,
        interesPctPorCuota: Number(interesPct) || 0,
      });
      setCuotasPreview(datos.cuotas);
      setPaso(4);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo previsualizar las cuotas.", "error");
    }
  }

  async function guardar() {
    if (!expedienteId) {
      mostrar("Seleccioná un expediente.", "error");
      return;
    }
    setGuardando(true);
    try {
      await api.post("/honorarios", {
        expediente_id: expedienteId,
        modalidad,
        moneda,
        porcentaje: resultadoSimulado?.tipo === "estimado" ? resultadoSimulado.porcentaje : null,
        honorario_estimado: subtotalNum,
        articulo_aplicado: resultadoSimulado?.tipo === "estimado" ? resultadoSimulado.articulo : null,
        jus_aplicados: resultadoSimulado?.tipo === "estimado" ? resultadoSimulado.jusAplicados : null,
        valor_jus_usado: valorJusResuelto,
        porcentaje_aplicado: resultadoSimulado?.tipo === "estimado" ? resultadoSimulado.porcentaje : null,
        fuente_normativa: resultadoSimulado?.tipo === "estimado" ? resultadoSimulado.fuente : null,
        subtotal_antes_descuento: subtotalNum,
        descuento_tipo: descuentoTipo === "Ninguno" ? null : descuentoTipo,
        descuento_valor: descuentoTipo === "Ninguno" ? null : Number(descuentoValor) || 0,
        motivo_descuento: motivoDescuento || null,
        cuotas:
          Number(cantidadCuotas) > 0
            ? {
                cantidadCuotas: Number(cantidadCuotas),
                frecuencia,
                primeraFecha,
                anticipo: Number(anticipo) || 0,
                interesPctPorCuota: Number(interesPct) || 0,
              }
            : undefined,
      });
      mostrar("Honorario simulado y guardado.", "exito");
      onCreado?.();
      cerrar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el honorario.", "error");
    } finally {
      setGuardando(false);
    }
  }

  const LEYENDA_LEGAL =
    "Cálculo estimativo sujeto a revisión profesional. No constituye regulación judicial ni reemplaza el criterio del tribunal competente.";

  return (
    <Modal titulo="Presupuestador de Honorarios" abierto={abierto} onCerrar={cerrar} ancho="max-w-2xl">
      <div className="mb-4 flex items-center gap-2 text-xs font-medium text-texto-secundario">
        {["Contexto", "Cálculo legal", "Propuesta comercial", "Revisión"].map((etiqueta, i) => (
          <span key={etiqueta} className={`flex items-center gap-2 ${paso === i + 1 ? "text-secundario" : ""}`}>
            {i > 0 && <span className="text-borde">/</span>}
            {i + 1}. {etiqueta}
          </span>
        ))}
      </div>

      {paso === 1 && (
        <div className="space-y-4">
          <Campo etiqueta="Cliente" obligatorio>
            <Select value={clienteId} onChange={(e) => { setClienteId(e.target.value); setExpedienteId(""); }}>
              <option value="">Seleccioná un cliente</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre_completo}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Expediente">
            <Select value={expedienteId} onChange={(e) => setExpedienteId(e.target.value)} disabled={!clienteId}>
              <option value="">Sin expediente asociado</option>
              {expedientes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.numero_expediente}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Materia">
            <Select value={materiaId} onChange={(e) => setMateriaId(e.target.value)}>
              <option value="">Seleccioná una materia</option>
              {materias.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </Select>
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Moneda">
              <Select value={moneda} onChange={(e) => setMoneda(e.target.value as Moneda)}>
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </Select>
            </Campo>
            <Campo etiqueta="Fecha">
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </Campo>
          </div>
          <div className="flex justify-end">
            <Boton onClick={() => setPaso(2)} disabled={!expedienteId}>
              Siguiente
            </Boton>
          </div>
        </div>
      )}

      {paso === 2 && (
        <div className="space-y-4">
          <Campo etiqueta="Regla aplicable" obligatorio>
            <Select value={regla} onChange={(e) => { setRegla(e.target.value as Regla); setResultadoSimulado(null); }}>
              <option value="manual">Manual (sin motor de cálculo)</option>
              <option value="art2">Art. 2 — Escala general de 1ª instancia</option>
              <option value="art10">Art. 10 — Mínimo no valuable pecuniariamente</option>
              <option value="art15">Art. 15 — Adicional por apelación</option>
            </Select>
          </Campo>
          {regla === "art2" && (
            <Campo etiqueta="Monto del proceso">
              <Input type="number" value={montoProceso} onChange={(e) => setMontoProceso(e.target.value)} />
            </Campo>
          )}
          {regla === "art15" && (
            <>
              <Campo etiqueta="Base calculada (ej. resultado de Art. 2/10)" obligatorio>
                <Input type="number" value={baseCalculada} onChange={(e) => setBaseCalculada(e.target.value)} />
              </Campo>
              <Campo etiqueta="Resultado de la apelación" obligatorio>
                <Select value={resultadoApelacion} onChange={(e) => setResultadoApelacion(e.target.value as ResultadoApelacion)}>
                  <option value="confirmada">Confirmada</option>
                  <option value="revocada_total">Revocada totalmente</option>
                  <option value="revocada_parcial">Revocada parcialmente</option>
                </Select>
              </Campo>
            </>
          )}
          {regla === "manual" && (
            <Campo etiqueta="Honorario sugerido" obligatorio>
              <Input type="number" value={honorarioManual} onChange={(e) => setHonorarioManual(e.target.value)} />
            </Campo>
          )}

          {resultadoSimulado?.tipo === "estimado" && (
            <div className="rounded-lg border border-borde bg-fondo p-3 text-sm">
              <p>
                <strong>{resultadoSimulado.articulo}</strong> — {resultadoSimulado.jusAplicados} JUS
                {resultadoSimulado.porcentaje != null && ` · ${resultadoSimulado.porcentaje}%`}
              </p>
              <p className="mt-1 text-base font-semibold text-texto">
                Subtotal legal: ${resultadoSimulado.subtotalLegal.toLocaleString("es-AR")}
              </p>
            </div>
          )}
          {resultadoSimulado?.tipo === "requiere_criterio_profesional" && (
            <div className="rounded-lg border border-advertencia/30 bg-advertencia/10 p-3 text-sm text-texto">
              {resultadoSimulado.motivo}
            </div>
          )}
          <p className="text-xs text-texto-secundario">{LEYENDA_LEGAL}</p>

          <div className="flex justify-between">
            <Boton variante="secundario" onClick={() => setPaso(1)}>
              Atrás
            </Boton>
            <div className="flex gap-2">
              {regla !== "manual" && (
                <Boton variante="secundario" onClick={calcular} cargando={calculando}>
                  Calcular
                </Boton>
              )}
              <Boton onClick={regla === "manual" ? calcular : () => setPaso(3)} disabled={regla !== "manual" && resultadoSimulado?.tipo !== "estimado"}>
                Siguiente
              </Boton>
            </div>
          </div>
        </div>
      )}

      {paso === 3 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Modalidad">
              <Select value={modalidad} onChange={(e) => setModalidad(e.target.value as ModalidadHonorario)}>
                {MODALIDADES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Honorario sugerido (ajustable)" obligatorio>
              <Input type="number" value={subtotal} onChange={(e) => setSubtotal(e.target.value)} />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Descuento">
              <Select value={descuentoTipo} onChange={(e) => setDescuentoTipo(e.target.value as TipoDescuentoHonorario | "Ninguno")}>
                <option value="Ninguno">Sin descuento</option>
                <option value="Porcentaje">Porcentaje</option>
                <option value="Importe">Importe fijo</option>
              </Select>
            </Campo>
            {descuentoTipo !== "Ninguno" && (
              <Campo etiqueta={descuentoTipo === "Porcentaje" ? "Descuento (%)" : "Descuento ($)"}>
                <Input type="number" value={descuentoValor} onChange={(e) => setDescuentoValor(e.target.value)} />
              </Campo>
            )}
          </div>
          {descuentoTipo !== "Ninguno" && (
            <Campo etiqueta="Motivo del descuento" obligatorio={montoDescuentoLocal / (subtotalNum || 1) > 0.2}>
              <TextArea value={motivoDescuento} onChange={(e) => setMotivoDescuento(e.target.value)} rows={2} />
            </Campo>
          )}
          <div className="rounded-lg border border-borde bg-fondo p-3 text-sm">
            <p>Honorario legal estimado: ${subtotalNum.toLocaleString("es-AR")}</p>
            <p>Descuento: ${montoDescuentoLocal.toLocaleString("es-AR")}</p>
            <p className="font-semibold text-texto">Total ofrecido: ${totalOfrecidoLocal.toLocaleString("es-AR")}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Entrega inicial (anticipo)">
              <Input type="number" value={anticipo} onChange={(e) => setAnticipo(e.target.value)} />
            </Campo>
            <Campo etiqueta="Cantidad de cuotas">
              <Input type="number" min={1} value={cantidadCuotas} onChange={(e) => setCantidadCuotas(e.target.value)} />
            </Campo>
            <Campo etiqueta="Frecuencia">
              <Select value={frecuencia} onChange={(e) => setFrecuencia(e.target.value as FrecuenciaCuotas)}>
                {FRECUENCIAS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Primer vencimiento">
              <Input type="date" value={primeraFecha} onChange={(e) => setPrimeraFecha(e.target.value)} />
            </Campo>
            <Campo etiqueta="Interés por cuota (%)">
              <Input type="number" value={interesPct} onChange={(e) => setInteresPct(e.target.value)} />
            </Campo>
          </div>
          <div className="flex justify-between">
            <Boton variante="secundario" onClick={() => setPaso(2)}>
              Atrás
            </Boton>
            <Boton onClick={previsualizarCuotas} disabled={!subtotal}>
              Siguiente
            </Boton>
          </div>
        </div>
      )}

      {paso === 4 && (
        <div className="space-y-4">
          <div className="rounded-lg border border-borde bg-fondo p-3 text-sm">
            <p>Honorario legal estimado: ${subtotalNum.toLocaleString("es-AR")}</p>
            <p>Descuento: ${montoDescuentoLocal.toLocaleString("es-AR")}</p>
            <p className="font-semibold text-texto">Total ofrecido: ${totalOfrecidoLocal.toLocaleString("es-AR")}</p>
            <p>Forma de pago: {cuotasPreview.length} cuota(s) {frecuencia}, anticipo ${Number(anticipo) || 0}</p>
          </div>
          {cuotasPreview.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="text-texto-secundario">
                <tr>
                  <th className="pb-1">Cuota</th>
                  <th className="pb-1">Vencimiento</th>
                  <th className="pb-1">Importe</th>
                </tr>
              </thead>
              <tbody>
                {cuotasPreview.map((cu) => (
                  <tr key={cu.numero} className="border-t border-borde">
                    <td className="py-1">{cu.numero}</td>
                    <td className="py-1">{cu.vencimiento}</td>
                    <td className="py-1">${cu.importeTotal.toLocaleString("es-AR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-texto-secundario">{LEYENDA_LEGAL}</p>
          <div className="flex justify-between">
            <Boton variante="secundario" onClick={() => setPaso(3)}>
              Atrás
            </Boton>
            <Boton onClick={guardar} cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </div>
      )}
    </Modal>
  );
}
